import type { UiRole } from '../services/entity-resolution';
import { isInternalRole } from '../util/roles';

/**
 * Built features whose entry points are hidden until product launches them,
 * and who sees each one in the meantime. Each flag hides only the control that
 * opens the feature; the feature itself (routes, services, dialogs) stays in
 * place, so changing who sees one is a one-line change here.
 *
 * A flag names an audience per backend (`util/env.ts`):
 *   - `qa`: the QA backend, which is QA, local dev and Vitest
 *   - `prod`: the production backend
 * and each audience is one of:
 *   - `off`: nobody
 *   - `internal`: INTERNAL_ADMIN and INTERNAL_POST_PROCESSING (`isInternalRole`),
 *     in whichever account they are looking at, Log In As included
 *   - a list of roles, e.g. `['INTERNAL_ADMIN']` or `['CUSTOMER_USER']`: exactly
 *     those, each the role in the account being looked at (`UiRole`, so a
 *     misspelled role does not compile)
 *   - `everyone`
 *
 * Read a flag with `useReleaseFlag()` (src/hooks/), which follows the role
 * across account switches. A flag is temporary: once both backends read
 * `everyone` and the launch has held, delete the entry; the compiler then
 * lists every check to remove. Hiding a control is not access control:
 * anything a role must not do, the backend refuses.
 *
 * Code only for now, so a change of audience ships with a deploy. If a shared
 * runtime flag service is adopted, `useReleaseFlag` is where it plugs in and no
 * call site changes (banked in `docs/engineering-backlog.md`).
 */
export type ReleaseAudience =
  | 'off'
  | 'internal'
  | 'everyone'
  | readonly UiRole[];

export interface ReleaseFlag {
  /** The ticket that owns the launch. */
  ticket: string;
  /** Who sees the feature against the QA backend: QA, local dev and Vitest. */
  qa: ReleaseAudience;
  /** Who sees it against the production backend. */
  prod: ReleaseAudience;
}

export const RELEASE_FLAGS = {
  /** Photoset sharing (DV-2102): the Manage Assets header's Share control
   *  (Internal Share, PlanOtour and the gallery quick menu). The
   *  public gallery route keeps serving any link already minted. */
  photosetShare: { ticket: 'DV-2102', qa: 'everyone', prod: 'off' },
  /** The floor plan tier cards' Example button and image zoom. */
  floorPlanExamples: { ticket: 'DV-3204', qa: 'off', prod: 'off' }
} satisfies Record<string, ReleaseFlag>;

export type ReleaseFlagName = keyof typeof RELEASE_FLAGS;

export interface ReleaseViewer {
  /** The realm role in the account being looked at (`getScopeRole()`). */
  role: string | null;
  /** `targetsProductionBackend()`. */
  productionBackend: boolean;
}

/** Whether this viewer sees the feature `flag` guards. */
export const releaseFlagOn = (
  flag: ReleaseFlag,
  viewer: ReleaseViewer
): boolean => {
  const audience = viewer.productionBackend ? flag.prod : flag.qa;
  if (audience === 'everyone') return true;
  if (audience === 'off') return false;
  if (audience === 'internal') return isInternalRole(viewer.role);
  return audience.some((role) => role === viewer.role);
};
